import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import {
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  Icon,
  IconContainer,
  LegendCircle,
} from "../../../components/visuals/styledComponents";
import { greyText, lightGreyOutline, lightGreyText } from "../../../styles";
import { ToggleAccordionBlueText } from "../../../components/buttons/ToggleAccordionBlueText";
import failCircle from "../../../assets/svg/implicit_temperature_fail.svg";
import successCircle from "../../../assets/svg/implicit_temperature_success.svg";

const StyledRowContainer = styled(RowContainer)`
  padding-bottom: 10px;
  border-bottom: 1px solid ${lightGreyOutline};
`;

interface TPILegendAccordionItemProps {
  level: number;
  color: string;
  title: string;
  isActive: boolean;
  setActiveLevel: (level: number | null) => void;
  questions: {
    id: string;
    text: string;
    status: boolean;
  }[];
}

export const TPILegendAccordionItem = ({
  level,
  color,
  title,
  questions,
  isActive,
  setActiveLevel,
}: TPILegendAccordionItemProps): JSX.Element => {
  const legendRef = useRef<HTMLDivElement | null>(null);
  const scrollToLegend = (): void => {
    legendRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  };

  useEffect(() => {
    if (isActive) {
      scrollToLegend();
    }
  }, [isActive]);

  const handleChange = (open: boolean): void => {
    setActiveLevel(open ? level : null);
  };

  return (
    <StyledRowContainer ref={legendRef} jc="flex-start" ai="flex-start">
      <FlexibleContainer fd="column" ai="flex-start">
        <FlexibleContainer fd="column" ai="flex-start">
          <ElementContainer ai="center" jc="flex-start">
            <LegendCircle color={color} />
            <CompassParagraph color={lightGreyText}>
              Level {level}
            </CompassParagraph>
          </ElementContainer>

          <ElementContainer ai="center">
            <LegendCircle color="transparent" />
            <ToggleAccordionBlueText
              handleChange={handleChange}
              textActive={title}
              textInactive={title}
              color={greyText}
              fontSize={13}
              bold={false}
              isOpen={isActive}
            />
          </ElementContainer>

          {isActive &&
            questions.map((question, index) => (
              <FlexibleContainer
                key={question.id || `${level}-${index}`}
                jc="flex-start"
                w="100%"
                p="0px 20px"
              >
                <IconContainer>
                  <Icon
                    src={question.status ? successCircle : failCircle}
                    alt="question-status-icon"
                  />
                </IconContainer>
                <CompassParagraph color={greyText}>
                  {`${question.id.split("_")[2]}: ${question.text}`}
                </CompassParagraph>
              </FlexibleContainer>
            ))}
        </FlexibleContainer>
      </FlexibleContainer>
    </StyledRowContainer>
  );
};
